/**
 * formatCop — COP currency + date formatters for the billing/cobros UI.
 *
 * All helpers are null-safe: null/undefined input returns the em-dash "—".
 * The COP formatter uses es-CO locale so thousands separators are dots and
 * the currency symbol matches Colombian convention ($ or COP prefix/suffix).
 */

import { decomposeDuration } from "./relativeTime.js";

const _copFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format an integer COP amount for display.
 *
 * @param {number|null|undefined} amount - Integer cents-free COP amount.
 * @returns {string}
 */
export function formatCop(amount) {
  if (amount == null) return "—";
  return _copFormatter.format(amount);
}

/**
 * Format a date value as DD/MM/YYYY (es-CO short date).
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export function formatDateShort(value) {
  if (value == null) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  // Use timeZone: "UTC" so ISO date-only strings (e.g. "2026-05-17") are
  // treated as the calendar date they represent rather than shifted by the
  // local offset (which can roll the day back by 1 in UTC-5/Colombia).
  return d.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Format a timestamp for report tables/cells as "jue 21 may, 14:32".
 *
 * Absolute, locale-aware (es-CO), 24h clock. Use this in the Reports module
 * (velocidades, estacionamientos, paradas, recorridos) — never relative
 * "hace X horas" strings. The weekday + month-abbr layout gives enough
 * context to read without expanding column widths.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
const _reportDateTimeFormatter = new Intl.DateTimeFormat("es-CO", {
  weekday: "short",
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

// Strip trailing punctuation (period or comma) that some ICU/locale revisions
// append to weekday or month tokens — e.g. "jue., 21 may., 14:32" → "jue 21 may".
function _cleanToken(value) {
  return value.replace(/[.,]+$/, "");
}

export function formatReportDateTime(value) {
  if (value == null) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  // formatToParts() gives us locale-correct token values without depending on
  // the exact punctuation/separators ICU chooses for es-CO. We compose the
  // canonical shape ourselves: "jue 21 may, HH:mm".
  const parts = _reportDateTimeFormatter.formatToParts(d);
  const get = (type) =>
    _cleanToken(parts.find((p) => p.type === type)?.value ?? "");
  const weekday = get("weekday");
  const day = get("day");
  const month = get("month");
  const hour = get("hour");
  const minute = get("minute");
  return `${weekday} ${day} ${month}, ${hour}:${minute}`;
}

/**
 * Format a duration in minutes for human-readable report UI.
 *
 *   <60 min  → "5 min" / "45 min" / "0 min"
 *   <24 h    → "1 h" / "1 h 30 min" / "7 h 30 min"
 *   ≥24 h    → "1 día 5 h" / "3 meses 2 días" / "1 año 3 meses 2 días 5 h"
 *
 * Estándar de duraciones del aplicativo: nunca acumular una sola unidad
 * ("5.000 h" prohibido) — se descompone en años/meses/días/h/min mostrando
 * cada unidad con valor (ver lib/relativeTime.js `decomposeDuration`).
 *
 * Use this wherever a duration value is shown to the user (summary cards,
 * popups, table cells). Raw "170 min" / "450 min" strings are forbidden —
 * they force mental arithmetic.
 *
 * Export columns (CSV/PDF) intentionally keep raw integer minutes so the
 * end-customer can do spreadsheet math; use this helper for screen renders
 * only.
 *
 * @param {number|null|undefined} totalMinutes
 * @returns {string}
 */
export function formatDurationMinutes(totalMinutes) {
  if (totalMinutes == null) return "—";
  const n = Number(totalMinutes);
  if (!Number.isFinite(n)) return "—";
  const minutes = Math.max(0, Math.round(n));
  if (minutes < 60) return `${minutes} min`;
  const parts = decomposeDuration(minutes * 60);
  const out = [];
  if (parts.years > 0)
    out.push(parts.years === 1 ? "1 año" : `${parts.years} años`);
  if (parts.months > 0)
    out.push(parts.months === 1 ? "1 mes" : `${parts.months} meses`);
  if (parts.days > 0)
    out.push(parts.days === 1 ? "1 día" : `${parts.days} días`);
  if (parts.hours > 0) out.push(`${parts.hours} h`);
  if (parts.minutes > 0) out.push(`${parts.minutes} min`);
  return out.join(" ");
}

/**
 * Formatea una distancia en km para UI (es-CO): ≥100 km sin decimales,
 * <100 km con 1 decimal. No incluye la unidad "km".
 *
 * Null-safe like the other helpers: null/undefined/NaN → "—".
 *
 * NO es intercambiable con geo.js `formatDistance(km)`: aquél usa toFixed(1)
 * SIEMPRE, cae a metros bajo 1 km y anexa la unidad ("850 m" / "1.2 km"), sin
 * locale. Éste aplica separador de miles es-CO, decimales variables y sin
 * unidad (el prefijo "≈ … km" lo pone el caller). Contratos distintos a
 * propósito — no consolidar.
 *
 * @param {number|null|undefined} km
 * @returns {string}
 */
export function formatDistanceKm(km) {
  if (km == null) return "—";
  const n = Number(km);
  if (!Number.isFinite(n)) return "—";
  const decimals = n >= 100 ? 0 : 1;
  return n.toLocaleString("es-CO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formatea la lectura de un ODÓMETRO (es-CO): "15.041". Entero siempre y sin
 * la unidad — el "km" lo pone el caller o el rótulo de la columna.
 *
 * Tercer helper de distancia, y a propósito. `formatDistanceKm` mete UN
 * DECIMAL por debajo de 100 km, que en un tablero no existe: nadie lee
 * "15.041,0 km", y en una moto recién dada de alta saldría "80,5". Y
 * `geo.js formatDistance` cae a metros bajo 1 km ("850 m"), que para un
 * odómetro es directamente incorrecto. Los tres contratos son distintos —
 * seguir la nota de `formatDistanceKm` y NO consolidar.
 *
 * Null-safe como el resto: null/undefined/NaN → "—".
 *
 * @param {number|string|null|undefined} km
 * @returns {string}
 */
export function formatOdometer(km) {
  if (km == null || km === "") return "—";
  const n = Number(km);
  if (!Number.isFinite(n)) return "—";
  return Math.trunc(n).toLocaleString("es-CO");
}

/**
 * Format a date value as "17 de mayo de 2026" (es-CO long date with month name).
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export function formatDateLong(value) {
  if (value == null) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  // Use timeZone: "UTC" for the same reason as formatDateShort.
  return d.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
